import { Component, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { CancelledError } from '@tanstack/react-query';
import { ErrorFallback } from './ErrorFallback';

// React Query throws this when an observer unsubscribes mid-fetch (e.g. during
// a route transition triggered by discarding a draft). It is an internal
// signal, not a user-actionable error, and must not surface as a fallback UI.
const isCancelledError = (error: unknown): boolean => error instanceof CancelledError;

interface ErrorBoundaryProps extends WithTranslation {
  children: ReactNode;
  fallback?: (error: Error, resetError: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree and displays a fallback UI.
 *
 * @example
 * ```tsx
 * <ErrorBoundary fallback={(error, reset) => <ErrorFallback error={error} resetError={reset} />}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    if (isCancelledError(error)) {
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (isCancelledError(error)) return;

    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by ErrorBoundary:', error);
      console.error('Error info:', errorInfo);
    }

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Log to error tracking service (e.g., Sentry) in production
    // Example: Sentry.captureException(error);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps, prevState: ErrorBoundaryState) {
    // Only an error from before this update: the page that failed in this render already sees the new key.
    if (prevState.hasError && this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.resetError();
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Render custom fallback if provided, otherwise use ErrorFallback
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      // Default to ErrorFallback component
      return <ErrorFallback error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

// Wrap with withTranslation HOC to provide translation support
export const ErrorBoundary = withTranslation()(ErrorBoundaryClass);
